import { Routes } from '@angular/router';
import { DashboardComponent } from './dashboard/dashboard.component';
import { autoLoginGuard } from './shared/guards/auto-login.guard';
import { ForbiddenComponent } from './forbidden/forbidden.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { VendorServerComponent } from './vendor-server/vendor-server.component';
import { UserProfileComponent } from './user-profile/user-profile.component';
import { CreateDashboardComponent } from './dashboard/create-dashboard/create-dashboard.component';
import { DashboardListComponent } from './dashboard/dashboard-list/dashboard-list.component';
import { DashboardResolver } from './dashboard/resolvers/dashboard.resolver';
import { UpdateDashboardComponent } from './dashboard/update-dashboard/update-dashboard.component';
import { RoleGuard } from './shared/guards/role.guard';
import { PermissionGuard } from './shared/guards/permission.guard';
import { NectaUserRole } from './shared/user/models/user';
import { PermissionCode } from './shared/user/models/permission';
import { UserProfileResolver } from './shared/user/resolver/user-profile.resolver';
import { NectaFrameComponent } from './necta/necta-frame/necta-frame.component';
import { AdminClientApplicationComponent } from './admin-client-application/admin-client-application.component';
import { SuperAdminGuard } from './shared/guards/super-admin.guard';
import { NectaSystemAdminGuard } from './shared/guards/necta-system-admin.guard';
import { NectaModuleResolver } from './necta-module/resolver/necta-module.resolver';
import { ModuleUploadGuard } from './necta-module/guards/module-upload.guard';
import { DashboardByTenantResolver } from './dashboard/resolvers/dashboards-by-tenant.resolver';
import { FlexDashboardComponent } from './dashboard/flex-dashboard/flex-dashboard.component';
import { WelcomeDashboardResolver } from './dashboard/resolvers/welcome-dashboard.resolver';
import { TenantReadyGuard } from './shared/guards/tenant-ready.guard';
import { DashboardManagementGuard } from './shared/guards/dashboard-management.guard';
import { designSystemEnabledGuard } from './shared/guards/design-system-enabled.guard';

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'dashboard/welcome' },
  {
    path: 'dashboard/welcome',
    component: FlexDashboardComponent,
    canActivate: [autoLoginGuard, TenantReadyGuard],
    runGuardsAndResolvers: 'always',
    resolve: {
      dashboard: WelcomeDashboardResolver
    },
    // Seitenkopf kommt aus app-dashboard-header (SectionHeader), Breadcrumb unterdrücken
    data: { breadcrumb: null }
  },
  {
    path: 'dashboard/roadmap',
    loadComponent: () =>
      import('./dashboard/roadmap/roadmap-dashboard.component')
        .then(m => m.RoadmapDashboardComponent),
    canActivate: [autoLoginGuard, TenantReadyGuard],
    data: { breadcrumb: null }
  },
  { path: 'dashboard/-1/view', pathMatch: 'full', redirectTo: 'dashboard/welcome' },
  {
    path: 'dashboard/:id/view',
    component: DashboardComponent,
    canActivate: [autoLoginGuard, TenantReadyGuard],
    resolve: {
      dashboard: DashboardResolver
    },
    data: {
            breadcrumb: [
            { label: 'Dashboard', url: '/dashboard' },
            { label: (data: any) => data.dashboard.name }
          ]  }
  },
  {
    path: 'dashboard/create',
    component: CreateDashboardComponent,
    canActivate: [autoLoginGuard, DashboardManagementGuard, RoleGuard],
    data: { roles: [NectaUserRole.Sysadmin] }
  },
  {
    path: 'dashboard/list',
    component: DashboardListComponent,
    canActivate: [autoLoginGuard, TenantReadyGuard, DashboardManagementGuard, RoleGuard],
    data: { roles: [NectaUserRole.Sysadmin] },
    resolve: {
      dashboards: DashboardByTenantResolver
    }
  },
  {
    path: 'dashboard/:id/update',
    component: UpdateDashboardComponent,
    canActivate: [autoLoginGuard, TenantReadyGuard, DashboardManagementGuard, RoleGuard],
    data: { roles: [NectaUserRole.Sysadmin] },
    resolve: {
      dashboard: DashboardResolver
    }
  },
  {
    path: 'necta/:id/view',
    component: NectaFrameComponent,
    canActivate: [autoLoginGuard],
    resolve: {
      user: UserProfileResolver
    }
  },
  {
    path: 'forbidden',
    component: ForbiddenComponent,
    canActivate: [autoLoginGuard],
  },
  {
    path: 'necta-supply',
    component: VendorServerComponent,
    canActivate: [autoLoginGuard],
  },
  {
    path: 'client-application-management',
    component: AdminClientApplicationComponent,
    canActivate: [SuperAdminGuard] // nur SuperAdmin darf die Komponente sehen
  },
  {
    path: 'userprofile',
    component: UserProfileComponent,
    canActivate: [autoLoginGuard],
  },{
    path: 'necta-modules/:id/view',
    loadComponent: () =>
      import('./necta-module/components/view-necta-module/view-necta-module.component')
        .then(m => m.ViewNectaModuleComponent),
    resolve: {
      moduleUrl: NectaModuleResolver
    }
  },{
    path: 'necta-modules/create',
    canActivate: [autoLoginGuard, TenantReadyGuard, PermissionGuard],
    canDeactivate: [ModuleUploadGuard],
    data: { permissions: [PermissionCode.Sysadmin] },
    loadComponent: () =>
      import('./necta-module/components/create-necta-module/create-necta-module.component')
        .then(m => m.CreateNectaModuleComponent),
  },{
    path: 'necta-modules/list',
    canActivate: [autoLoginGuard, TenantReadyGuard, PermissionGuard],
    data: { permissions: [PermissionCode.Sysadmin] },
    loadComponent: () =>
      import('./necta-module/components/necta-module-list/necta-module-list.component')
        .then(m => m.NectaModuleListComponent),
  },{
    path: 'necta-modules/:moduleId/edit',
    canActivate: [autoLoginGuard, TenantReadyGuard, PermissionGuard],
    data: { permissions: [PermissionCode.Sysadmin] },
    loadComponent: () =>
      import('./necta-module/components/edit-necta-module/edit-necta-module.component')
        .then(m => m.EditNectaModuleComponent),
  },

  // TODO(design-system): temporär – vor Merge nach main entfernen oder freischalten.
  {
    path: 'design-system',
    canActivate: [autoLoginGuard, designSystemEnabledGuard],
    loadChildren: () =>
      import('./design-system/design-system.routes').then(m => m.DESIGN_SYSTEM_ROUTES),
  },
  {
    path: 'qassandra',
    loadComponent: () =>
      import('./qassandra/components/qassandra-workspace/qassandra-workspace.component')
        .then(m => m.QassandraWorkspaceComponent),
    canActivate: [autoLoginGuard, TenantReadyGuard],
  },
  {
    // Mandantenübergreifende Qassandra-Auswertung für Necta-Mitarbeiter. Bewusst ohne
    // TenantReadyGuard: die Seite hängt an keinem aktiven Mandanten.
    path: 'service/qassandra',
    loadComponent: () =>
      import('./qassandra/admin/components/qassandra-admin-page/qassandra-admin-page.component')
        .then(m => m.QassandraAdminPageComponent),
    canActivate: [autoLoginGuard, NectaSystemAdminGuard],
    // Seitenkopf kommt aus app-section-header, Breadcrumb unterdrücken
    data: { breadcrumb: null }
  },

  { path: 'unauthorized', component: UnauthorizedComponent },
];

//TODO Dashboard Routes anpassen (Default + Child)